import type React from 'react';
import { AdornedInput, FormField, FormHelp, Label, Textarea } from '@zillow/constellation-10';

import type { Section } from '../../../../models/index';
import { PROPERTY_TYPE } from '../../../../constants';
import { stopPropagationIfInvalidNumber } from '../../../../utils/validatorTypes/PositiveIntegerValidator';

export function isMultiFamily(section: Section): boolean {
  return section.getFieldValue(PROPERTY_TYPE.FIELD_NAME.TYPE_MULTI_FAMILY) === true;
}

type Props = {
  section: Section;
  utilityName: string;
  percentageFieldName: string;
  formulaFieldName: string;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function MultiFamilyLandlordUtility({
  formulaFieldName,
  percentageFieldName,
  section,
  setSectionValues,
  utilityName,
}: Props): JSX.Element {
  function onPercentageChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const value = event.currentTarget.value;

    setSectionValues({
      [percentageFieldName]: value,
    });
  }

  function onFormulaChange(event: React.ChangeEvent<HTMLTextAreaElement>): void {
    const value = event.currentTarget.value;

    setSectionValues({
      [formulaFieldName]: value,
    });
  }

  return (
    <>
      <FormField
        required
        css={{ marginBottom: 'layout.tight' }}
        label={<Label>{`What percentage of the monthly ${utilityName} cost will you bill the tenant?`}</Label>}
        control={
          <AdornedInput.Root>
            <AdornedInput.Input
              aria-label="Enter percentage"
              onKeyDown={stopPropagationIfInvalidNumber}
              onChange={onPercentageChange}
              type="number"
            />
            <AdornedInput.Adornment>%</AdornedInput.Adornment>
          </AdornedInput.Root>
        }
      />
      <FormField
        required
        label={<Label>{`What is your utilities allocation formula for ${utilityName}?`}</Label>}
        control={
          <Textarea
            aria-label="Utilities allocation formula"
            css={{ resize: 'vertical' }}
            value={section.getFieldValue(formulaFieldName)}
            onChange={onFormulaChange}
          />
        }
        description={
          <FormHelp>
            Explain formula used to determine percentage. Example: The building has 5 units. The tenant lives in 1, so
            their share is 1/5 (or 20%) of the total cost.
          </FormHelp>
        }
      />
    </>
  );
}
